<template>
  <div
    v-bind="$attrs"
    ref="wrapper"
    :class="{ 'max-h-[--max-height] overflow-hidden': isClamped }"
    :style="{
      '--max-height':
        appliedMaxLines !== undefined
          ? `${appliedMaxLines * leading}rem`
          : undefined,
      '--max-lines': appliedMaxLines,
    }"
  >
    <slot />
  </div>

  <app-button
    v-if="showViewMore"
    class="mt-2"
    :color="triggerColor"
    :size="triggerSize"
    variant="link"
    @click="toggle"
  >
    {{ toggleLabel }}
  </app-button>
</template>

<script lang="ts" setup>
import type { ButtonColor, ButtonSize } from "../../button/button.model";

defineOptions({
  inheritAttrs: false,
});

const properties = withDefaults(
  defineProps<{
    maxLines: number;
    triggerColor?: ButtonColor;
    triggerSize?: ButtonSize;
    leading?: number;
  }>(),
  {
    leading: 1.75,
    triggerColor: "accent",
    triggerSize: undefined,
  },
);

const emit = defineEmits(["clamp", "unclamp"]);

const wrapperElement = templateRef<HTMLDivElement>("wrapper");

const appliedMaxLines = ref<number | undefined>(properties.maxLines);
const isClamped = computed(() => appliedMaxLines.value === properties.maxLines);

const toggle = () => {
  appliedMaxLines.value =
    appliedMaxLines.value === properties.maxLines
      ? undefined
      : properties.maxLines;

  emit(isClamped.value ? "clamp" : "unclamp");
};

const { t } = useI18n();
const toggleLabel = computed(() =>
  isClamped.value ? t("app.cta.view_more") : t("app.cta.view_less"),
);

const showViewMore = ref(false);
const isVisible = useElementVisibility(wrapperElement);

whenever(isVisible, () => {
  showViewMore.value =
    wrapperElement.value.clientHeight < wrapperElement.value.scrollHeight;
});
</script>
